import React, { useState } from 'react';
import { Col, Label, Alert } from 'reactstrap';
import ActionButton from '../../../../Components/Common/ActionButton';
import { confirmRemove } from '../../../../utils/confirm';
import { toggleProps } from '../../../../utils/a11y';
import { hasClauseResponse } from '../../../../shared/offer-domain/respond';
import { getOwnClauseResponse } from '../../../../shared/offer-domain/respond';
import { canRespondOnOfferDetail } from '../../utils/offerEditAccess';
import { RespondNotesEditor } from '../../../../shared/offer-domain/ui';
import { COUNTER_ACTION_BTN_STYLE } from '../../../../shared/offer-domain/constants';

const MandatoryClauseBadge = () => (
  <span className='badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill fw-medium fs-11'>
    Mandatory
  </span>
);

const ClauseHtmlContent = ({ value }) => {
  if (!value) {
    return <div className='text-dark mb-0 lh-base fs-13'>-</div>;
  }
  return (
    <div
      className='text-dark mb-0 lh-base fs-13'
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

const ClauseCard = ({
  clause,
  index,
  clauseResponses,
  draftNotes,
  expandedClauses,
  formMain,
  isClauseResponsesSaving,
  isClauseResponsesLoading,
  handleDraftChange,
  handleClauseSecondaryAction,
  handleClauseSave,
  toggleClauseExpand,
  setErrors,
  canRespond: canRespondProp,
  allowConfirm = false,
  isConfirmed = false,
  onConfirm,
  showHeader = true,
  showDescription = true,
}) => {
  const savedResponse = getOwnClauseResponse(clauseResponses[clause.id], {
    isEvaluator: false,
  });
  const savedNotes = savedResponse?.respondNotes ?? '';
  const hasSaved = hasClauseResponse(savedNotes);

  const isExpanded =
    expandedClauses[clause.id] !== undefined
      ? Boolean(expandedClauses[clause.id])
      : hasSaved;

  const draftValue = draftNotes[clause.id] ?? savedNotes;
  const isDraftDirty = String(draftValue ?? '') !== String(savedNotes ?? '');
  const canRespond = canRespondProp ?? canRespondOnOfferDetail(formMain);

  const [isEditing, setIsEditing] = useState(false);
  const isMandatory = clause.isShowToDMUT === true;
  const canExpandRespond = canRespond && !isMandatory;
  const showRespondActions = canExpandRespond;

  return (
    <Col sm={12} className='mb-2'>
      <div
        className={`rounded-3 overflow-hidden bg-white ${
          isExpanded && canExpandRespond
            ? 'border border-primary shadow-sm'
            : 'border'
        }`}
      >
        {showHeader && (
          <div className='px-3 py-3 d-flex align-items-center gap-3 border-bottom bg-light-subtle'>
            {index != null && (
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-12 ${
                  isExpanded && canExpandRespond
                    ? 'bg-primary text-white'
                    : 'bg-light text-muted border'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                {index}
              </span>
            )}
            <div className='flex-grow-1' style={{ minWidth: 0 }}>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span
                  className={`fs-14 mb-0 ${
                    isExpanded && canExpandRespond
                      ? 'fw-bold text-dark'
                      : 'fw-semibold text-dark'
                  }`}
                >
                  {clause.clauseTitle}
                </span>
                {isMandatory && <MandatoryClauseBadge />}
              </div>
            </div>
          </div>
        )}

        <div className='p-3'>
          {showDescription && isMandatory && (
            <div className='text-danger small fw-medium mb-2'>
              Mandatory clause — confirmation only
            </div>
          )}
          {showDescription && (
            <div className='p-3 rounded-3 bg-light-subtle border text-muted fs-13 lh-base'>
              <ClauseHtmlContent value={clause.clauseDescription} />
            </div>
          )}

          <div
            className={`${showDescription ? 'border-top pt-3 mt-3 ' : ''}d-flex flex-wrap gap-2 justify-content-between align-items-center`}
          >
            <div
              className='d-flex align-items-center gap-2'
              style={canExpandRespond ? { cursor: 'pointer' } : undefined}
              {...(canExpandRespond
                ? toggleProps(
                    () => toggleClauseExpand(clause.id),
                    isExpanded,
                    undefined,
                    'expanded'
                  )
                : {})}
            >
              {canExpandRespond ? (
                <span
                  className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                    isExpanded
                      ? 'bg-primary-subtle text-primary'
                      : 'bg-light text-muted'
                  }`}
                  style={{ width: '28px', height: '28px' }}
                >
                  <i
                    className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                    aria-hidden='true'
                  ></i>
                </span>
              ) : null}
              {hasSaved && !isMandatory ? (
                <>
                  <i
                    className='ri-checkbox-circle-fill text-success fs-16'
                    aria-hidden='true'
                  ></i>
                  <span className='fw-semibold text-success fs-13'>
                    Responded
                  </span>
                </>
              ) : allowConfirm && (isConfirmed || (isMandatory && hasSaved)) ? (
                <>
                  <i
                    className='ri-checkbox-circle-fill text-success fs-16'
                    aria-hidden='true'
                  ></i>
                  <span className='fw-semibold text-success fs-13'>
                    Confirmed
                  </span>
                </>
              ) : (
                <span className='text-muted fs-13'>
                  {isMandatory ? 'Pending confirmation' : 'No response note'}
                </span>
              )}
            </div>

            {canRespond && (
              <div className='d-flex flex-wrap gap-2'>
                {allowConfirm && (isMandatory || !hasSaved) ? (
                  <ActionButton
                    variant={
                      isConfirmed || (isMandatory && hasSaved)
                        ? 'secondary'
                        : 'primary'
                    }
                    size='sm'
                    icon='ri-check-line'
                    disabled={
                      isConfirmed ||
                      (isMandatory && hasSaved) ||
                      isClauseResponsesLoading ||
                      isClauseResponsesSaving
                    }
                    onClick={onConfirm}
                  >
                    {isConfirmed || (isMandatory && hasSaved)
                      ? 'Confirmed'
                      : 'Confirm'}
                  </ActionButton>
                ) : null}
                {showRespondActions ? (
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-reply-line'
                    style={COUNTER_ACTION_BTN_STYLE}
                    onClick={() => {
                      if (!isExpanded) toggleClauseExpand(clause.id);
                      if (hasSaved) setIsEditing(true);
                    }}
                    disabled={isClauseResponsesLoading || hasSaved || isEditing}
                  >
                    Respond
                  </ActionButton>
                ) : null}
              </div>
            )}
          </div>

          {canExpandRespond && isExpanded && (
            <div className='mt-3'>
              {hasSaved && !isEditing ? (
                <div className='p-3 rounded-3 border-start border-3 border-danger bg-danger-subtle mt-2'>
                  <div className='fw-bold text-dark fs-13 mb-2'>
                    Respond Notes
                  </div>
                  <div className='text-secondary fs-13 mb-3'>
                    <ClauseHtmlContent value={savedNotes} />
                  </div>

                  <div className='d-flex flex-wrap align-items-center gap-2 gap-sm-3'>
                    <ActionButton
                      variant='secondary'
                      size='sm'
                      icon='ri-edit-line'
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </ActionButton>
                    <ActionButton
                      variant='destructive'
                      size='sm'
                      icon='ri-delete-bin-line'
                      confirm={false}
                      onClick={async () => {
                        const confirmed = await confirmRemove(
                          'response note',
                          clause.clauseTitle,
                          {
                            message:
                              'Your response note will be permanently deleted. This cannot be undone.',
                          }
                        );
                        if (!confirmed) return undefined;
                        const deleting = handleClauseSecondaryAction(
                          clause.id,
                          true
                        );
                        setIsEditing(false);
                        return deleting;
                      }}
                    >
                      Remove
                    </ActionButton>
                  </div>
                </div>
              ) : (
                <div className='mt-2'>
                  <div className='d-flex flex-wrap gap-1 justify-content-between align-items-center mb-2'>
                    <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                      Respond Notes
                    </Label>
                    {isDraftDirty && (
                      <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line fs-14'></i>
                        Unsaved Changes
                      </span>
                    )}
                  </div>

                  <RespondNotesEditor
                    id={clause.id}
                    value={draftValue}
                    onChange={e => {
                      handleDraftChange(clause.id, e.target.value);
                      if (typeof setErrors === 'function') {
                        setErrors(prev => ({ ...prev, [clause.id]: '' }));
                      }
                    }}
                    isDraftDirty={isDraftDirty}
                  />

                  <div className='d-flex flex-wrap gap-2 mt-3 justify-content-end'>
                    <ActionButton
                      variant='secondary'
                      icon='ri-close-line'
                      onClick={() => {
                        if (hasSaved) {
                          setIsEditing(false);
                        } else {
                          toggleClauseExpand(clause.id);
                        }
                      }}
                      disabled={isClauseResponsesSaving}
                    >
                      Cancel
                    </ActionButton>

                    <ActionButton
                      variant='primary'
                      icon={
                        isClauseResponsesSaving
                          ? undefined
                          : 'ri-send-plane-line'
                      }
                      onClick={async () => {
                        await handleClauseSave(clause.id);
                        setIsEditing(false);
                      }}
                      loading={isClauseResponsesSaving}
                      disabled={isClauseResponsesSaving || !isDraftDirty}
                    >
                      {isClauseResponsesSaving ? 'Saving...' : 'Send Response'}
                    </ActionButton>
                  </div>
                </div>
              )}
            </div>
          )}

          {!canRespond && hasSaved && !isMandatory && (
            <Alert
              className='bg-success-subtle text-success alert-solid alert-label-icon mb-0 mt-3'
              fade={false}
            >
              <i className='ri-chat-3-line label-icon'></i>
              <div>
                <div className='fw-semibold text-dark mb-1 fs-13'>
                  Respond Notes
                </div>
                <ClauseHtmlContent value={savedNotes} />
              </div>
            </Alert>
          )}
        </div>
      </div>
    </Col>
  );
};

export default ClauseCard;
